import PropTypes from 'prop-types';
import Pusher from 'pusher-js';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFetch } from '../api/utils';

const PusherContext = createContext(null);

/**
 * Pusher Provider Component
 *
 * Manages a singleton Pusher client connection and provides subscription
 * functionality to child components.
 */
export function PusherProvider({ children }) {
  const pusherRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const fetcher = useFetch();

  // Store fetcher in ref to keep authorizer stable
  const fetcherRef = useRef(fetcher);

  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  // Stable authorizer function - only created once
  const authorizer = useRef((channel) => {
    return {
      authorize: async (socketId, callback) => {
        try {
          const response = await fetcherRef.current({
            path: 'v1/numina/pusher/auth/',
            method: 'POST',
            data: JSON.stringify({
              socket_id: socketId,
              channel_name: channel.name,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
          });
          callback(null, response);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Pusher auth error:', error);
          callback(error, null);
        }
      },
    };
  }).current;

  useEffect(() => {
    // Initialize Pusher client (singleton)
    if (pusherRef.current) {
      return undefined;
    }

    // Get Pusher key from environment variable
    const pusherKey = process.env.REACT_APP_PUSHER_KEY;
    const pusherCluster = process.env.REACT_APP_PUSHER_CLUSTER || 'us2';

    if (!pusherKey) {
      // eslint-disable-next-line no-console
      console.warn('REACT_APP_PUSHER_KEY not configured. Real-time updates will not work.');
      return undefined;
    }

    pusherRef.current = new Pusher(pusherKey, {
      cluster: pusherCluster,
      encrypted: true,
      authorizer,
    });

    pusherRef.current.connection.bind('connected', () => {
      setIsConnected(true);
    });

    pusherRef.current.connection.bind('disconnected', () => {
      setIsConnected(false);
    });

    pusherRef.current.connection.bind('error', (err) => {
      // eslint-disable-next-line no-console
      console.error('Pusher connection error:', err);
    });

    // Cleanup on unmount
    return () => {
      if (pusherRef.current) {
        pusherRef.current.disconnect();
        pusherRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // authorizer is stable via useRef

  const value = useMemo(
    () => ({
      pusher: pusherRef.current,
      isConnected,
    }),
    [isConnected],
  );

  return <PusherContext.Provider value={value}>{children}</PusherContext.Provider>;
}

PusherProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access Pusher instance
 */
export function usePusher() {
  const context = useContext(PusherContext);
  if (context === undefined) {
    throw new Error('usePusher must be used within a PusherProvider');
  }
  return context;
}

/**
 * Hook to subscribe to a Pusher channel
 *
 * @param {string} channelName - The channel to subscribe to
 * @param {Object} events - Object mapping event names to handler functions
 * @returns {Object} - { channel, isSubscribed }
 */
export function usePusherChannel(channelName, events = {}) {
  const { pusher } = usePusher();
  const [channel, setChannel] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Store latest event handlers in ref to avoid rebinding on every render
  const eventHandlersRef = useRef({});

  // Update ref when handlers change
  useEffect(() => {
    eventHandlersRef.current = events;
  }, [events]);

  useEffect(() => {
    if (!pusher || !channelName) {
      return undefined;
    }

    // Subscribe to channel
    const newChannel = pusher.subscribe(channelName);

    newChannel.bind('pusher:subscription_succeeded', () => {
      setIsSubscribed(true);
    });

    newChannel.bind('pusher:subscription_error', (error) => {
      // eslint-disable-next-line no-console
      console.error(`Error subscribing to channel ${channelName}:`, error);
      setIsSubscribed(false);
    });

    setChannel(newChannel);

    // Cleanup
    return () => {
      if (newChannel) {
        pusher.unsubscribe(channelName);
        setIsSubscribed(false);
        setChannel(null);
      }
    };
  }, [pusher, channelName]);

  // Bind event handlers - only rebind if channel changes
  useEffect(() => {
    if (!channel) {
      return undefined;
    }

    // Create wrapper functions that call the latest handler from ref
    const boundHandlers = {};
    const eventNames = Object.keys(events);

    eventNames.forEach((eventName) => {
      const wrapper = (data) => {
        if (eventHandlersRef.current[eventName]) {
          eventHandlersRef.current[eventName](data);
        }
      };
      boundHandlers[eventName] = wrapper;
      channel.bind(eventName, wrapper);
    });

    // Cleanup - unbind the specific wrapper functions we bound
    return () => {
      Object.entries(boundHandlers).forEach(([eventName, wrapper]) => {
        channel.unbind(eventName, wrapper);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, channelName]); // events stored in ref to avoid rebinding

  return { channel, isSubscribed };
}
